import { useEffect } from 'react';
import { getDefaultStore, useAtomValue } from 'jotai';
import { useToast } from '@chakra-ui/react';
import { WS_EVENTS } from '@twinden/shared';
import { api } from '../lib/api';
import {
  currentFamilyAtom,
  currentUserAtom,
  familiesAtom,
  listItemsByListAtom,
  listsByFamilyAtom,
  setCurrentFamilyAtom,
  setFamiliesAtom,
} from '../store';
import { t } from '../i18n';
import { useWebSocket } from './useWebSocket';

interface MemberLeftPayload {
  familyId?: string;
  userId?: string;
  deleted?: boolean;
  newRootId?: string;
  newRootName?: string;
}

type Store = ReturnType<typeof getDefaultStore>;

/**
 * Someone left a family or deleted their account (M51): the family's
 * members and roles are fetched again, its cached lists dropped (their
 * items may have been assigned to them, D45), and a new root is told.
 */
export async function applyMemberLeft(
  payload: MemberLeftPayload,
  store: Store,
  notify: (title: string, description: string) => void
) {
  const { familyId } = payload;
  if (!familyId) return;
  const me = store.get(currentUserAtom)?.id;
  if (payload.newRootId && payload.newRootId === me) {
    const familyName =
      store.get(familiesAtom).find((family) => family.id === familyId)?.name ??
      '';
    notify(
      t('account.family.nowRoot', { name: familyName }),
      t('account.family.nowRootBody')
    );
  }

  const lists = store.get(listsByFamilyAtom)[familyId] ?? [];
  if (lists.length) {
    const items = { ...store.get(listItemsByListAtom) };
    for (const list of lists) delete items[list.id];
    store.set(listItemsByListAtom, items);
    const byFamily = { ...store.get(listsByFamilyAtom) };
    delete byFamily[familyId];
    store.set(listsByFamilyAtom, byFamily);
  }

  try {
    store.set(setFamiliesAtom, (await api.getFamilies()).data);
    if (store.get(currentFamilyAtom)?.id === familyId) {
      store.set(setCurrentFamilyAtom, (await api.getFamily(familyId)).data);
    }
  } catch {
    // The next load catches up.
  }
}

/** Mounted once, in the app shell. */
export function useFamilyMemberLeft() {
  const { onEvent } = useWebSocket();
  const toast = useToast();
  // Re-subscribe when the signed-in person changes.
  const userId = useAtomValue(currentUserAtom)?.id;
  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        if (event !== WS_EVENTS.FAMILY_MEMBER_LEFT) return;
        void applyMemberLeft(
          payload as MemberLeftPayload,
          getDefaultStore(),
          (title, description) =>
            toast({ title, description, status: 'success', duration: 8000 })
        );
      }),
    [onEvent, toast, userId]
  );
}
